import { useCallback, useEffect, useState } from 'react'

/**
 * React hook that returns the current `window.location.search` parameters as a
 * `URLSearchParams` instance and keeps them in sync with browser navigation.
 *
 * The returned `URLSearchParams` reference is stable across renders as long as
 * the query string is semantically unchanged, making it safe to use in
 * dependency arrays.
 */
export function useUrlSearchParams(): URLSearchParams {
	const [searchParams, setSearchParams] = useState(() => {
		if (typeof window === 'undefined') {
			return new URLSearchParams()
		}
		return new URLSearchParams(window.location.search)
	})

	const update = useCallback(() => {
		const next = new URLSearchParams(window.location.search)
		setSearchParams((prev) => {
			if (prev.toString() === next.toString()) {
				return prev
			}
			return next
		})
	}, [])

	useEffect(() => {
		if (typeof window === 'undefined') {
			return undefined
		}
		const ac = new AbortController()

		// Fallback in case browser doesn't support navigation api
		// May miss in-page navigations using history.pushState() or
		// history.replaceState()
		window.addEventListener('popstate', update, { signal: ac.signal })

		// Note: navigation api with currententrychange event is baseline as of
		// January 2026 (https://developer.mozilla.org/en-US/docs/Web/API/Navigation/currententrychange_event)
		window.navigation?.addEventListener?.('currententrychange', update, {
			signal: ac.signal,
		})
		update()
		return () => {
			ac.abort()
		}
	}, [update])

	return searchParams
}
